import React, { useEffect, useMemo, useState, useCallback } from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import { Col, Row, Form, Overlay, Popover, Button } from "react-bootstrap";
import DatePicker from "react-datepicker";

import { withNavigation } from "../../../../utils/wrapper";
import { toDateOrNull } from "../../../../utils/object";
import Address from "../../../../layout/address";
import CodeSelect from "../../../../layout/select/code";
import GenerationsSelect from "../../../../layout/select/generation";
import Hamburger from "../../../../layout/kebab";
import ConfirmModal from "../../../../layout/modal";

function normalizePerson(person = {}) {
  return {
    ...(person || {}),
    ssn: String(person?.ssn ?? "").replace(/\D/g, "").slice(0, 9),
    birthDate: toDateOrNull(person?.birthDate),
    Birthplace: {
      ...(person?.Birthplace || {}),
    },
  };
}

function normalizeComparable(value) {
  if (value == null) return "";
  if (value instanceof Date) return value.toISOString();
  if (typeof value === "object") return JSON.stringify(value);
  return String(value).trim();
}

function toDisplayDate(value) {
  if (!value) return null;
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return null;

  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
}

function formatSuggestionValue(value, path = "") {
  if (value == null || value === "") return "[blank]";

  const lowerPath = String(path || "").toLowerCase();
  if (lowerPath.includes("date")) {
    return toDisplayDate(value) || String(value);
  }

  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (typeof value === "object") return JSON.stringify(value, null, 2);
  return String(value);
}

function getByPath(obj, path) {
  if (!path) return undefined;
  return String(path)
    .split(".")
    .reduce((acc, key) => (acc == null ? undefined : acc[key]), obj);
}

function Child(props) {
  const {
    index,
    uuid,
    _cid,
    Person,
    legalCustody,
    physicalCustody,
    onChange,
    onDelete,
    backgroundColor,
    suggestions,
    onApplySuggestion,
    onRejectSuggestion,
  } = props;

  const [uuidState, setUuidState] = useState(uuid ?? null);
  const [cidState, setCidState] = useState(_cid ?? null);
  const [personState, setPersonState] = useState(() => normalizePerson(Person));
  const [legalCustodyState, setLegalCustodyState] = useState(legalCustody ?? "joint");
  const [physicalCustodyState, setPhysicalCustodyState] = useState(physicalCustody ?? "joint");
  const [menuState, setMenuState] = useState({
    show: false,
    target: null,
  });
  const [modalState, setModalState] = useState({
    confirm: {
      show: false,
      title: "",
      body: "",
      onConfirm: null,
    },
  });
  const [suggestionMenu, setSuggestionMenu] = useState({
    show: false,
    target: null,
    path: null,
  });

  useEffect(() => {
    setUuidState(uuid ?? null);
    setCidState(_cid ?? null);
    setPersonState(normalizePerson(Person));
    setLegalCustodyState(legalCustody ?? "joint");
    setPhysicalCustodyState(physicalCustody ?? "joint");
  }, [uuid, _cid, Person, legalCustody, physicalCustody]);

  const closeHamburger = useCallback(() => {
    setMenuState({
      show: false,
      target: null,
    });
  }, []);

  const closeConfirm = useCallback(() => {
    setModalState((prev) => ({
      ...prev,
      confirm: {
        ...prev.confirm,
        show: false,
        title: "",
        body: "",
        onConfirm: null,
      },
    }));
  }, []);

  const openConfirm = useCallback(({ title, body, onConfirm }) => {
    setModalState((prev) => ({
      ...prev,
      confirm: {
        show: true,
        title,
        body,
        onConfirm,
      },
    }));
  }, []);

  const toSSNDigits = useCallback(
    (value) => String(value ?? "").replace(/\D/g, "").slice(0, 9),
    []
  );

  const toDashedSSN = useCallback(
    (digits) => {
      const d = toSSNDigits(digits);
      const a = d.slice(0, 3);
      const b = d.slice(3, 5);
      const c = d.slice(5, 9);
      if (d.length <= 3) return a;
      if (d.length <= 5) return `${a}-${b}`;
      return `${a}-${b}-${c}`;
    },
    [toSSNDigits]
  );

  const ssnDisplay = useMemo(
    () => toDashedSSN(personState?.ssn),
    [personState?.ssn, toDashedSSN]
  );

  const currentSnapshot = useMemo(() => ({
    uuid: uuidState,
    _cid: cidState,
    Person: normalizePerson(personState),
    legalCustody: legalCustodyState,
    physicalCustody: physicalCustodyState,
  }), [uuidState, cidState, personState, legalCustodyState, physicalCustodyState]);

  const emitChange = useCallback(
    ({ nextUuid, nextCid, nextPerson, nextLegalCustody, nextPhysicalCustody }) => {
      if (!onChange) return;

      onChange({
        index,
        values: {
          uuid: nextUuid ?? uuidState,
          _cid: nextCid ?? cidState,
          Person: normalizePerson(nextPerson ?? personState),
          legalCustody: nextLegalCustody ?? legalCustodyState,
          physicalCustody: nextPhysicalCustody ?? physicalCustodyState,
        },
      });
    },
    [
      onChange,
      index,
      uuidState,
      cidState,
      personState,
      legalCustodyState,
      physicalCustodyState,
    ]
  );

  const updatePerson = useCallback(
    (patch = {}) => {
      setPersonState((prev) => {
        const nextPerson = normalizePerson({
          ...(prev || {}),
          ...(patch || {}),
        });
        emitChange({ nextPerson });
        return nextPerson;
      });
    },
    [emitChange]
  );

  const onSSNChange = useCallback(
    (value) => {
      updatePerson({ ssn: toSSNDigits(value) });
    },
    [updatePerson, toSSNDigits]
  );

  const onRequestDelete = useCallback(
    (e) => {
      e?.preventDefault?.();
      closeHamburger();

      const displayName =
        [personState?.firstName, personState?.lastName].filter(Boolean).join(" ") ||
        `Child #${index + 1}`;

      openConfirm({
        title: "Remove child",
        body: `Are you sure you want to remove ${displayName}?`,
        onConfirm: () => {
          onDelete?.({ index });
          closeConfirm();
        },
      });
    },
    [
      closeHamburger,
      personState?.firstName,
      personState?.lastName,
      index,
      openConfirm,
      onDelete,
      closeConfirm,
    ]
  );

  const openHamburger = useCallback((event) => {
    event.preventDefault();
    setMenuState({
      show: true,
      target: event.currentTarget,
    });
  }, []);

  const closeSuggestionMenu = useCallback(() => {
    setSuggestionMenu({
      show: false,
      target: null,
      path: null,
    });
  }, []);

  const openSuggestionMenu = useCallback((event, path) => {
    event.preventDefault();
    event.stopPropagation();
    setSuggestionMenu({
      show: true,
      target: event.currentTarget,
      path,
    });
  }, []);

  const hasSuggestion = useCallback((path) => {
    const incoming = getByPath(suggestions || {}, path);
    if (incoming === undefined) return false;

    const current = getByPath(currentSnapshot, path);
    return normalizeComparable(current) !== normalizeComparable(incoming);
  }, [suggestions, currentSnapshot]);

  const applySuggestion = useCallback(() => {
    const path = suggestionMenu.path;
    if (!path) return;

    const value = getByPath(suggestions || {}, path);
    if (onApplySuggestion) onApplySuggestion(path, value);
    closeSuggestionMenu();
  }, [suggestionMenu.path, suggestions, onApplySuggestion, closeSuggestionMenu]);

  const rejectSuggestion = useCallback(() => {
    const path = suggestionMenu.path;
    if (!path) return;

    const value = getByPath(suggestions || {}, path);
    if (onRejectSuggestion) onRejectSuggestion(path, value);
    closeSuggestionMenu();
  }, [suggestionMenu.path, suggestions, onRejectSuggestion, closeSuggestionMenu]);

  const renderLabel = useCallback((text, path) => {
    const showIcon = hasSuggestion(path);

    return (
      <Form.Label>
        {text}
        {showIcon ? (
          <button
            type="button"
            onClick={(e) => openSuggestionMenu(e, path)}
            style={{
              border: 0,
              background: "transparent",
              padding: 0,
              marginLeft: 6,
              lineHeight: 1,
              fontSize: "0.95rem",
              cursor: "pointer",
            }}
            title="Review imported survey value"
          >
            ✨
          </button>
        ) : null}
      </Form.Label>
    );
  }, [hasSuggestion, openSuggestionMenu]);

  const activeSuggestionValue = suggestionMenu.path
    ? getByPath(suggestions || {}, suggestionMenu.path)
    : undefined;
  
  const applyNestedSuggestion = useCallback((prefix, path, value) => {
    if (!onApplySuggestion) return;
    onApplySuggestion(`${prefix}.${path}`, value);
  }, [onApplySuggestion]);

  const rejectNestedSuggestion = useCallback((prefix, path, value) => {
    if (!onRejectSuggestion) return;
    onRejectSuggestion(`${prefix}.${path}`, value);
  }, [onRejectSuggestion]);

  return (
    <>
      <Col
        xs="12"
        style={{ backgroundColor, padding: "20px", borderBottom: "1px solid black" }}
      >
        <Row className="g-3">
          <Col xs="11">
            <h3 className="small-title">Child #{index + 1}</h3>
          </Col>
          <Col xs="1">
            <Hamburger variant="link" vertical={false} onClick={openHamburger} />
          </Col>
        </Row>

        <Row className="g-3">
          <Col lg="4">
            <div className="mb-3 top-label">
              <Form.Control
                type="text"
                name="firstName"
                value={personState?.firstName ?? ""}
                onChange={(e) => updatePerson({ firstName: e.target.value })}
              />
              {renderLabel("FIRST NAME", "Person.firstName")}
            </div>
          </Col>

          <Col lg="2">
            <div className="mb-3 top-label">
              <Form.Control
                type="text"
                name="middleName"
                value={personState?.middleName ?? ""}
                onChange={(e) => updatePerson({ middleName: e.target.value })}
              />
              {renderLabel("MIDDLE NAME", "Person.middleName")}
            </div>
          </Col>

          <Col lg="4">
            <div className="mb-3 top-label">
              <Form.Control
                type="text"
                name="lastName"
                value={personState?.lastName ?? ""}
                onChange={(e) => updatePerson({ lastName: e.target.value })}
              />
              {renderLabel("LAST NAME", "Person.lastName")}
            </div>
          </Col>

          <Col lg="2">
            <div className="mb-3 top-label">
              <GenerationsSelect
                placeholder=""
                name="generation"
                value={personState?.generation ?? ""}
                onChange={(e) => updatePerson({ generation: e?.value ?? "" })}
              />
              {renderLabel("SUFFIX", "Person.generation")}
            </div>
          </Col>
        </Row>

        <Row className="g-3">
          <Col md="4">
            <div className="mb-3 top-label">
              <Form.Control
                type="text"
                name="ssn"
                autoComplete="off"
                inputMode="numeric"
                placeholder="___-__-____"
                value={ssnDisplay}
                onChange={(e) => onSSNChange(e.target.value)}
              />
              {renderLabel("SOCIAL SECURITY #", "Person.ssn")}
            </div>
          </Col>

          <Col md="4">
            <div className="mb-3 top-label">
              <DatePicker
                isClearable
                id={`birthDate-child-${index}`}
                name="birthDate"
                className="form-control"
                selected={toDateOrNull(personState?.birthDate)}
                onChange={(date) => updatePerson({ birthDate: date })}
              />
              {renderLabel("BORN ON", "Person.birthDate")}
            </div>
          </Col>

          <Col md="4">
            <div className="mb-3 top-label">
              <CodeSelect
                codes={[
                  { value: "unknown", description: "Unknown" },
                  { value: "female", description: "Female" },
                  { value: "male", description: "Male" },
                  { value: "other", description: "Other" },
                ]}
                value={personState?.gender ?? "unknown"}
                onChange={(e) => updatePerson({ gender: e?.value ?? "unknown" })}
                placeholder=""
                name="gender"
              />
              {renderLabel("GENDER", "Person.gender")}
            </div>
          </Col>
        </Row>

        <Row className="g-3">
          <Col lg="12">
            <h2 className="small-title">Custody</h2>
          </Col>
        </Row>

        <Row className="g-3">
          <Col md="6">
            <div className="mb-3 top-label">
              <CodeSelect
                codes={[
                  { value: "joint", description: "Joint" },
                  { value: "tie-breaker (P)", description: "Tie-breaker (to Plaintiff)" },
                  { value: "tie-breaker (D)", description: "Tie-breaker (to Defendant)" },
                  { value: "sole (P)", description: "Sole (to Plaintiff)" },
                  { value: "sole (D)", description: "Sole (to Defendant)" },
                ]}
                value={legalCustodyState}
                onChange={(e) => {
                  const v = e?.value ?? "joint";
                  setLegalCustodyState(v);
                  emitChange({ nextLegalCustody: v });
                }}
                placeholder=""
                name="legalCustody"
              />
              {renderLabel("LEGAL CUSTODY", "legalCustody")}
            </div>
          </Col>

          <Col md="6">
            <div className="mb-3 top-label">
              <CodeSelect
                codes={[
                  { value: "joint", description: "Joint" },
                  { value: "primary (P)", description: "Primary (to Plaintiff)" },
                  { value: "primary (D)", description: "Primary (to Defendant)" },
                  { value: "sole (P)", description: "Sole (to Plaintiff)" },
                  { value: "sole (D)", description: "Sole (to Defendant)" },
                ]}
                value={physicalCustodyState}
                onChange={(e) => {
                  const v = e?.value ?? "joint";
                  setPhysicalCustodyState(v);
                  emitChange({ nextPhysicalCustody: v });
                }}
                placeholder=""
                name="physicalCustody"
              />
              {renderLabel("PHYSICAL CUSTODY", "physicalCustody")}
            </div>
          </Col>
        </Row>

        <Row className="g-3">
          <Col lg="12">
            <h2 className="small-title">Born At</h2>
          </Col>
        </Row>

        <Address
          {...(personState?.Birthplace || {})}
          simple
          name="Birthplace"
          id={`Birthplace-child-${index}`}
          suggestions={getByPath(suggestions || {}, "Person.Birthplace") || {}}
          onApplySuggestion={(path, value) => applyNestedSuggestion("Person.Birthplace", path, value)}
          onRejectSuggestion={(path, value) => rejectNestedSuggestion("Person.Birthplace", path, value)}
          onChange={(values) => {
            updatePerson({
              Birthplace: { ...(values || {}) },
            });
          }}
        />
      </Col>

      <Overlay
        target={menuState.target}
        show={menuState.show}
        placement="bottom-end"
        rootClose
        onHide={closeHamburger}
      >
        {(overlayProps) => (
          <Popover {...overlayProps}>
            <Popover.Body>
              <Row className="mb-2">
                <Col className="d-flex flex-row mt-1">
                  <button
                    type="button"
                    className="btn btn-link p-0 me-2 btn-sm small text-start"
                    onClick={onRequestDelete}
                  >
                    Remove
                  </button>
                </Col>
              </Row>
            </Popover.Body>
          </Popover>
        )}
      </Overlay>

      <Overlay
        target={suggestionMenu.target}
        show={suggestionMenu.show}
        placement="bottom-end"
        rootClose
        onHide={closeSuggestionMenu}
      >
        {(overlayProps) => (
          <Popover {...overlayProps}>
            <Popover.Header as="h3">Imported survey value</Popover.Header>
            <Popover.Body>
              <div className="small text-muted mb-2">{suggestionMenu.path}</div>
              <div className="mb-3" style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                {formatSuggestionValue(activeSuggestionValue, suggestionMenu.path)}
              </div>
              <div className="d-flex gap-2">
                <Button size="sm" variant="primary" onClick={applySuggestion}>
                  Accept
                </Button>
                <Button size="sm" variant="outline-secondary" onClick={rejectSuggestion}>
                  Reject
                </Button>
              </div>
            </Popover.Body>
          </Popover>
        )}
      </Overlay>

      <ConfirmModal
        {...modalState.confirm}
        buttons={[
          {
            caption: "ok",
            onEnabledClick: modalState.confirm.onConfirm,
            variant: "secondary",
            display: true,
            enabled: true
          },
          {
            default: true,
            caption: "cancel",
            onEnabledClick: closeConfirm,
            variant: "primary",
            display: true,
            enabled: true
          }
        ]}
      />
    </>
  );
}

Child.propTypes = {
  auth: PropTypes.object,
  index: PropTypes.number,
  uuid: PropTypes.string,
  _cid: PropTypes.string,
  backgroundColor: PropTypes.string,
  Person: PropTypes.object,
  physicalCustody: PropTypes.string,
  legalCustody: PropTypes.string,
  onChange: PropTypes.func,
  onDelete: PropTypes.func,
  suggestions: PropTypes.object,
  onApplySuggestion: PropTypes.func,
  onRejectSuggestion: PropTypes.func,
};

Child.defaultProps = {
  index: 0,
  uuid: null,
  _cid: null,
  backgroundColor: "white",
  Person: {},
  legalCustody: "joint",
  physicalCustody: "joint",
  onChange: null,
  onDelete: null,
  suggestions: {},
  onApplySuggestion: null,
  onRejectSuggestion: null,
};

const mapStateToProps = (state) => ({
  auth: state.auth,
});

export default withNavigation(
  connect(mapStateToProps, null, null, { forwardRef: true })(Child)
);